import React, {useRef, useState} from 'react';
import {
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogOverlay,
    Avatar,
    Box,
    Button,
    Flex,
    MenuList,
    Spacer,
    Spinner,
    Text,
    useToast
} from '@chakra-ui/react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useDeletePatientMutation,
    useGetPatientRecordsQuery,
    useGetSinglePatientQuery,
    useLazyGetImageUrlsQuery
} from "../store/patientApi/patientApiSlice.ts";
import {patientDetailsType} from "../utils/typesConstant/types.tsx";
import AddPatientModal from "../components/modals/AddPatientModal.tsx";
import EditSummaryModal from "../components/modals/EditSummaryModal.tsx";
import {
    AlertDialog,
    AlertDialogFooter,
    AlertDialogHeader,
    ArrowBackIcon,
    DeleteIcon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem
} from "@chakra-ui/icons";
import {FiMoreVertical} from "react-icons/fi";
import {useSelector} from "react-redux";
import {RootState} from "../store/store.ts";
import UploadDocumentModal from "../components/modals/UploadDocumentModal.tsx";
import NotFoundPage from "./NotFoundPage.tsx";


export default function PatientDetailsPage() {
    const toast = useToast();
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [isModalOpen, setIsModalOpen] = useState(false);//to manage either edit modal is Open or not
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);//to manage either upload modal is Open or not
    const [selectedSummary, setSelectedSummary] = useState<any>(null);//to manage which summary is clicked

    const [triggerImageFetch, {isFetching: isAttachmentLoading}] = useLazyGetImageUrlsQuery();
    const user = useSelector((state: RootState) => state?.user?.user)


    const {data, isLoading, isError} = useGetSinglePatientQuery(id);
    const {data: recordData, isLoading: recordLoading, isError: recordError} = useGetPatientRecordsQuery(id);
    const [isEditSummaryModalOpen, setIsEditSummaryModalOpen] = useState(false);
    const [recordToEdit, setRecordToEdit] = useState(null);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const cancelRef = useRef(null);
    const [deletePatient] = useDeletePatientMutation();
    const [isDeleting, setIsDeleting] = useState(false);


    const handleDeletePatient = async () => {
        setIsDeleting(true);

        try {
            const res = await deletePatient(patient.uid).unwrap();
            console.log("Deleted:", res.message);

            toast({
                title: "Patient deleted.",
                description: "The patient was deleted successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });

            setIsAlertOpen(false);
            navigate('/admin/patients');
        } catch (error) {
            console.error("Delete failed:", error);

            toast({
                title: "Deletion failed.",
                description: "Unable to delete the patient.",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });

            setIsAlertOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEditClick = () => {
        setIsModalOpen(true);
    };

    const handleShowAttachments = async () => {
        if (!selectedSummary?.record_id || selectedSummary.record_id.length !== 9) return;

        try {
            const result = await triggerImageFetch(selectedSummary.record_id).unwrap();
            const urls = result.data?.urls || [];
            console.log("Urls are", urls);

            if (urls.length === 0) {
                alert('No attachments found.');
                return;
            }

            const firstUrl = urls[0];
            const filename = firstUrl.split('/').pop() || 'file';

            if (firstUrl.endsWith('.docx')) {
                console.log('Inside If');
                const link = document.createElement('a');
                link.href = firstUrl;
                link.download = filename;
                link.click();
            } else if (firstUrl.endsWith('.pdf')) {
                const pdfWindow = window.open('', '_blank');
                if (pdfWindow) {
                    pdfWindow.document.write(`
                    <html lang="">
                        <head>
                         <title>PDF Viewer</title>
                         </head>
                        <body style="margin:0">
                         <embed src="${firstUrl}" type="application/pdf" width="100%" height="100%" />
                       </body>
                     </html>
                     `);
                }
            } else {
                console.log('Inside else for image view');
                navigate('/urlPhotoView', {state: {urls}});
            }
        } catch (error) {
            console.error("Failed to fetch image URLs:", error);
            alert('Failed to fetch attachments');
        }
    };

    if (isLoading || recordLoading)
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Spinner size="xl" color="blue"/>
            </Box>
        );

    if (isError || recordError || !data || !recordData || recordData?.length === 0) {
        return <NotFoundPage/>
    }

    const patient: patientDetailsType = data.data;

    return (
        <Box>
            {/* Full-width Header */}
            <Box bg="#F4F7FE" width="100%" py={4} px={8}>
                <Box display="flex" alignItems="center" gap={4}>
                    <IconButton
                        icon={<ArrowBackIcon boxSize={7}/>}
                        aria-label="Go back"
                        variant="ghost"
                        onClick={() => navigate(-1)}
                    />
                    <Text fontSize="3xl" fontWeight="bold" color="black">
                        Patient Report
                    </Text>
                </Box>
            </Box>

            <Flex p={6} gap={6} direction={{base: 'column', md: 'row'}}>
                {/* Left Section: Patient Info */}
                <Box
                    position="relative"
                    width={{base: "100%", md: "30%"}}
                    height="640px"
                    p={8}
                    bg="white"
                    boxShadow="md"
                    borderRadius="xl"
                    overflowY="auto"
                >
                    {user?.isSuperuser && (
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                icon={<FiMoreVertical size={20} color="blue"/>}
                                variant="outline"
                                size="sm"
                                position="absolute"
                                top="4"
                                right="4"
                                aria-label="Options"
                            />
                            <MenuList
                                minW="100px"
                                border="none"
                                boxShadow="md"
                                py="1"
                            >
                                <MenuItem
                                    icon={<DeleteIcon color="red" boxSize={4}/>}
                                    onClick={() => setIsAlertOpen(true)}
                                    color="red"
                                    py="1"
                                    fontSize="sm"
                                    height="30px"
                                >
                                    Delete
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    )}


                    <Box textAlign="center" mb={6}>
                        <Avatar name={patient.name} size="xl" bg="#F4F7FE" color="#422AFB" mb={4}/>
                        <Text fontSize="xl" fontWeight="bold">{patient.name}</Text>
                        <Text color="gray.500" fontSize="sm">ID: {patient.patient_id}</Text>
                    </Box>

                    {/* Detail Rows */}
                    <Box>
                        {[
                            ["Age", patient.age],
                            // ["Gender", patient.gender],
                            ["Phone", patient.phone_number],
                            ["Doctor", patient.doctorName],
                            ["Patient ID", patient.patient_id],
                            ["Created At", new Date(patient.created_at).toLocaleDateString()],
                            ["Updated At", new Date(patient.updated_at).toLocaleDateString()]
                        ].map(([label, value]) => (
                            <Flex key={label} mb={4}>
                                <Box width="40%" color="gray.600">{label}:</Box>
                                <Spacer/>
                                <Box width="60%" textAlign="right" fontWeight="semibold">{value}</Box>
                            </Flex>
                        ))}
                    </Box>

                    <Button
                        mt={10}
                        width="100%"
                        bg="#422AFB"
                        color="white"
                        _hover={{bg: "#5842ff"}}
                        onClick={handleEditClick}
                    >
                        Edit Details
                    </Button>
                </Box>
                {/* Right Section: Summary and patient History */}
                <Box
                    flex="1"
                    p={8}
                    bg="white"
                    boxShadow="md"
                    borderRadius="xl"
                    overflowY="scroll"
                    maxHeight='640px'
                >
                    {selectedSummary ? (
                        <Box>
                            <Flex justifyContent="space-between" alignItems="center" mb={4}>
                                <Button
                                    onClick={() => setSelectedSummary(null)}
                                    colorScheme="blue"
                                    variant="outline"
                                >
                                    Back to All Summaries
                                </Button>
                                <Button
                                    bg="#422AFB"
                                    color="white"
                                    marginRight={'20px'}
                                    _hover={{bg: "#5842ff"}}
                                    isDisabled={!selectedSummary || selectedSummary.record_id?.length !== 9}
                                    onClick={handleShowAttachments}
                                >
                                    {isAttachmentLoading ? <Spinner size="sm" color="white"/> : "Show Attachments"}
                                </Button>

                            </Flex>
                            <Box bgColor={"#F4F7FE"} p={6} borderRadius="25">
                                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                                    <Text fontSize="xl" fontWeight="bold">Detailed Summary</Text>
                                    <Button
                                        bg="#422AFB"
                                        color="white"
                                        _hover={{bg: "#5842ff"}}
                                        onClick={() => {
                                            setRecordToEdit(selectedSummary);
                                            setIsEditSummaryModalOpen(true);
                                        }}
                                    >
                                        Edit Report Details
                                    </Button>
                                </Flex>

                                <Text fontSize="lg" fontWeight="bold"
                                      color="#422AFB">{new Date(selectedSummary.next_visit).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric'
                                })}</Text>
                                <ReportDetailRow heading="Diagnosis" data={selectedSummary.diagnosis}/>
                                <ReportDetailRow heading="Treatment Surgery"
                                                 data={selectedSummary.prescribed_treatment}/>
                                <ReportDetailRow heading="Condition at time of Discharge"
                                                 data={selectedSummary.discharge_condition}/>
                                <ReportDetailRow heading="Medication" data={selectedSummary.medication}/>
                                <ReportDetailRow heading="Last Visit"
                                                 data={new Date(selectedSummary.last_visit).toLocaleDateString('en-GB', {
                                                     day: '2-digit',
                                                     month: 'long',
                                                     year: 'numeric'
                                                 })}/>
                                <ReportDetailRow heading="Follow Ups" data={selectedSummary.follow_up}/>
                                <ReportDetailRow heading="Summary" data={selectedSummary.summary}/>

                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Flex justify="space-between" align="center" mb={4}>
                                <Text fontSize="2xl" fontWeight="bold">
                                    History
                                </Text>

                                <Button
                                    bg="#422AFB"
                                    color="white"
                                    _hover={{bg: "#5842ff"}}
                                    // onClick={() => navigate('/upload', { state: { patientId: id } })}
                                    onClick={() => setIsUploadModalOpen(true)}
                                >
                                    Create Patient Report
                                </Button>
                            </Flex>

                            {recordLoading ? (
                                <Spinner size="md"/>
                            ) : recordError || !recordData || recordData.length === 0 ? (
                                <Text>No records found.</Text>
                            ) : (
                                <Box>
                                    {recordData.map((record, index) => (
                                        <Box
                                            key={index}
                                            bgColor={"#F4F7FE"}
                                            mb={4}
                                            p={4}
                                            borderRadius="25"
                                            cursor="pointer"
                                            _hover={{bg: "#E2E8F0"}}
                                            onClick={() => setSelectedSummary(record)}
                                        >
                                            <Text>
                                                <strong>{new Date(record.next_visit).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}</strong></Text>
                                            <Text mt={2}>
                                                <Text as="span" color="blue.500" fontWeight="bold">Summary: </Text>
                                                {record.summary}
                                            </Text>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Flex>

            {/* Edit Modal */}
            <AddPatientModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                patientDetails={patient}
            />
            <EditSummaryModal
                open={isEditSummaryModalOpen}
                setOpen={setIsEditSummaryModalOpen}
                recordDetails={recordToEdit}
                setRecordDetails={setSelectedSummary}
            />

            <UploadDocumentModal open={isUploadModalOpen} setOpen={setIsUploadModalOpen} patientId={patient.uid}/>

            <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsAlertOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red">
                            Delete Patient
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete this patient? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button onClick={() => setIsAlertOpen(false)}>
                                No
                            </Button>
                            <Button colorScheme="blue" onClick={handleDeletePatient} ml={3}
                                    isLoading={isDeleting}
                                    loadingText="Deleting...">
                                Yes
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </Box>
    );
}


const ReportDetailRow = ({heading, data}) => (
    <Text mt={2}>
        <Text as="span" color="#422AFB" fontWeight="bold">• {heading}: </Text>
        {data}
    </Text>
);


